import { groupBy, omit, sortBy } from 'lodash'
import { create } from 'zustand'
import {
    applyPromocode,
    billingAdded,
    billingSpent,
    changeRecurringPayment,
    createRecurringPayment,
    createStorageTariffUser,
    deposit,
    recurringPayment,
    removeStorageTariffUser,
    storageTariffs,
    storageTariffUser,
} from '../api/billing'
import { UserExpiringCoinsDTO } from '../api/gen/base/types/UserExpiringCoinsDTO'
import { getTariffByUuid, getTariffList, getUserTariff } from '../api/tafiff'
import {
    AppliedPromocodeDTO,
    ApplyPromocodeModel,
    BalanceIncreaseListApiV1BillingBalanceIncreaseGetParams,
    BalanceIncreaseOutputDTO,
    DepositCreateOutputDTO,
    RecurringPaymentDetailOutputDTO,
    ServiceChargeListApiV1BillingServiceChargeGetParams,
    ServiceChargeOutputDTO,
    StorageTariff,
    StorageTariffUser,
    TariffDetailApiV1TariffTariffUuidGetParams,
    TariffDTO,
    TariffListApiV1TariffGetParams,
    TariffListOutputDTO,
    UserTariffDTO,
    UserWalletDTO,
    WIDepositCreateModel,
} from '../swagger/dto'

interface Actions {
    applyPromocode: (data: ApplyPromocodeModel) => Promise<AppliedPromocodeDTO>
    payments: (sum: string) => Promise<void>
    deposit: (data: WIDepositCreateModel) => Promise<DepositCreateOutputDTO>
    setIsFetchBalance: (isFetchBalance: boolean) => void
    setBillingTab: (name?: string) => void
    fetchBillingListAdded: (
        params?: BalanceIncreaseListApiV1BillingBalanceIncreaseGetParams,
    ) => Promise<BalanceIncreaseOutputDTO>
    fetchBillingListSpent: (
        params?: ServiceChargeListApiV1BillingServiceChargeGetParams,
    ) => Promise<ServiceChargeOutputDTO>
    fetchTariffByUuid: (params: TariffDetailApiV1TariffTariffUuidGetParams) => Promise<TariffDTO>
    fetchUserTariff: () => Promise<UserTariffDTO>
    fetchTariffList: (params?: TariffListApiV1TariffGetParams) => Promise<TariffListOutputDTO>
    fetchRecurringPayment: () => Promise<RecurringPaymentDetailOutputDTO>
    fetchStorageTariffs: () => Promise<StorageTariffsGetList>
    reset: () => void
    setWallet: (wallet?: UserWalletDTO) => void
    resetErrorApplyPromocode: () => void
}

interface State {
    error?: string
    isLoading: boolean
    balance?: number
    isFullAccess: boolean
    isFetchBalance: boolean
    isApplyPromocode: boolean
    errorApplyPromocode?: string
    isLoadingApplyPromocode: boolean
    billingTab: string
    isLoadingBillingList: boolean
    isErrorFetchBillingList?: string
    isLoadingDeposit: boolean
    billingListAdded?: BalanceIncreaseOutputDTO
    billingListSpent?: ServiceChargeOutputDTO

    isLoadingFetchTariffByUuid: boolean
    errorFetchTariffByUuid?: string
    tariff?: TariffDTO

    isLoadingFetchUserTariff: boolean
    errorFetchUserTariff?: string
    userTariff?: UserTariffDTO

    isLoadingFetchTariffList: boolean
    errorFetchTariffList?: string
    tariffList?: TariffDTO[]

    isLoadingFetchRecurringPayment: boolean
    errorFetchRecurringPayment?: string
    recurringPayment?: RecurringPaymentDetailOutputDTO

    isLoadingFetchStorageTariffs: boolean
    errorFetchStorageTariffs?: string
    storageTariffs?: StorageTariff[]

    isLoadingFetchStorageTariffUser: boolean
    errorFetchStorageTariffUser?: string
    storageTariffUser?: StorageTariffUser[]

    coins: UserExpiringCoinsDTO[]
}

const initialState: State = {
    error: undefined,
    isLoading: false,
    balance: undefined,
    isFullAccess: false,
    isFetchBalance: false,
    isApplyPromocode: false,
    errorApplyPromocode: undefined,
    isLoadingApplyPromocode: false,
    isLoadingBillingList: false,
    isErrorFetchBillingList: undefined,
    billingTab: 'spent',
    isLoadingDeposit: false,
    billingListAdded: undefined,
    billingListSpent: undefined,
    isLoadingFetchUserTariff: false,
    errorFetchUserTariff: undefined,
    userTariff: undefined,

    isLoadingFetchTariffByUuid: false,
    errorFetchTariffByUuid: undefined,
    tariff: undefined,

    isLoadingFetchTariffList: false,
    errorFetchTariffList: undefined,
    tariffList: undefined,

    isLoadingFetchRecurringPayment: false,
    errorFetchRecurringPayment: undefined,
    recurringPayment: undefined,

    isLoadingFetchStorageTariffs: false,
    errorFetchStorageTariffs: undefined,
    storageTariffs: undefined,

    isLoadingFetchStorageTariffUser: false,
    errorFetchStorageTariffUser: undefined,
    storageTariffUser: undefined,

    coins: [],
}

export const useProfileStore = create<Actions & State>()((set, get) => ({
    ...initialState,
    setWallet: (wallet?: UserWalletDTO) => {
        const { coins, available_balance = 0 } = wallet || {}

        set({
            isFullAccess: available_balance > 0,
            balance: available_balance,
            coins,
        })
    },

    applyPromocode: async (data) => {
        try {
            set({
                isApplyPromocode: false,
                isFetchBalance: false,
                errorApplyPromocode: undefined,
                isLoadingApplyPromocode: true,
            })
            await applyPromocode(data)

            set({
                isApplyPromocode: true,
                isFetchBalance: true,
                isLoadingApplyPromocode: false,
            })
        } catch (error) {
            set({
                isLoadingApplyPromocode: false,
                errorApplyPromocode:
                    error?.response?.data?.detail?.error_message ??
                    'Произошла ошибка! Повторите попытку позже',
            })
        }
    },

    deposit: async (data) => {
        try {
            set({
                errorApplyPromocode: undefined,
                isLoadingDeposit: true,
            })
            const response = await deposit(data)
            window.open(
                response.action.payment_link_data.href,
                response.action.payment_link_data.target,
            )
            set({
                isLoadingDeposit: false,
            })
        } catch (error) {
            set({
                isLoadingDeposit: false,
                errorApplyPromocode:
                    'Произошла ошибка! Проверьте правильность ввода суммы пополнения и повторите попытку',
            })
        }
    },
    setIsFetchBalance: (isFetchBalance: boolean) => {
        set({
            isFetchBalance,
        })
    },

    setBillingTab: (name?: string) =>
        set({
            billingTab: name,
        }),

    fetchBillingListAdded: async (params) => {
        try {
            set({
                billingListAdded: undefined,
                billingListSpent: undefined,
                isLoadingBillingList: true,
                isErrorFetchBillingList: undefined,
            })

            const data = await billingAdded(params)

            set({
                billingListAdded: data,
                isLoadingBillingList: false,
            })
        } catch (error) {
            set({
                isLoadingBillingList: false,
                isErrorFetchBillingList: 'Ошибка получения списка платежей',
            })
        }
    },
    fetchBillingListSpent: async (params) => {
        try {
            set({
                billingListAdded: undefined,
                billingListSpent: undefined,
                isLoadingBillingList: true,
                isErrorFetchBillingList: undefined,
            })

            const data = await billingSpent(params)

            set({
                billingListSpent: data,
                isLoadingBillingList: false,
            })
        } catch (error) {
            set({
                isLoadingBillingList: false,
                isErrorFetchBillingList: 'Ошибка получения списка платежей',
            })
        }
    },
    fetchTariffByUuid: async (params) => {
        try {
            set({
                tariff: undefined,
                isLoadingFetchTariffByUuid: true,
                errorFetchTariffByUuid: undefined,
            })

            const data = await getTariffByUuid(params)

            set({
                tariff: data,
                isLoadingFetchTariffByUuid: false,
            })
        } catch (error) {
            set({
                isLoadingFetchTariffByUuid: false,
                errorFetchTariffByUuid: 'Ошибка получения списка платежей',
            })
        }
    },
    fetchUserTariff: async () => {
        try {
            set({
                userTariff: undefined,
                isLoadingFetchUserTariff: true,
                errorFetchUserTariff: undefined,
            })

            const data = await getUserTariff()

            set({
                userTariff: data,
                isLoadingFetchUserTariff: false,
            })
        } catch (error) {
            set({
                isLoadingFetchUserTariff: false,
                errorFetchUserTariff: 'Ошибка получения списка платежей',
            })
        }
    },

    fetchTariffList: async (params) => {
        try {
            set({
                tariffList: undefined,
                isLoadingFetchTariffList: true,
                errorFetchTariffList: undefined,
            })

            const data = await getTariffList(params)
            const dataBySort = sortBy(data.objects, 'price')
            const dataByTitle = groupBy(dataBySort, 'title')
            const tariffListByTitle = omit(dataByTitle, 'Гостевой')

            set({
                tariffListByTitle,
                tariffList: data.objects,
                isLoadingFetchTariffList: false,
            })
        } catch (error) {
            set({
                isLoadingFetchTariffList: false,
                errorFetchTariffList: 'Ошибка получения списка платежей',
            })
        }
    },

    fetchRecurringPayment: async () => {
        try {
            set({
                isLoadingFetchRecurringPayment: false,
                errorFetchRecurringPayment: undefined,
                recurringPayment: undefined,
            })

            const data = await recurringPayment()

            set({
                recurringPayment: data,
                isLoadingFetchRecurringPayment: false,
            })
        } catch (error) {
            set({
                isLoadingFetchRecurringPayment: false,
                errorFetchRecurringPayment: 'Ошибка получения списка регулярных платежей',
            })
        }
    },

    fetchStorageTariffs: async () => {
        try {
            set({
                isLoadingFetchStorageTariffs: false,
                errorFetchStorageTariffs: undefined,
                storageTariffs: undefined,
            })

            const data = await storageTariffs()

            set({
                storageTariffs: data.objects,
                isLoadingFetchStorageTariffs: false,
            })
        } catch (error) {
            set({
                isLoadingFetchStorageTariffs: false,
                errorFetchStorageTariffs: 'Ошибка получения списка',
            })
        }
    },

    fetchStorageTariffUser: async () => {
        try {
            set({
                isLoadingFetchStorageTariffUser: false,
                errorFetchStorageTariffUser: undefined,
            })

            const data = await storageTariffUser()

            set({
                storageTariffUser: data.objects,
                isLoadingFetchStorageTariffUser: false,
            })
        } catch (error) {
            set({
                isLoadingFetchStorageTariffUser: false,
                storageTariffUser: 'Ошибка получения списка',
            })
        }
    },

    createStorageTariffUser: async (payload) => {
        try {
            set({
                isLoadingCreateStorageTariffUser: true,
                errorCreateStorageTariffUser: undefined,
            })

            await createStorageTariffUser(payload)



            set({
                // storageTariffUser: storageTariffUser,
                isLoadingCreateStorageTariffUser: false,
            })
        } catch (error) {
            set({
                isLoadingCreateStorageTariffUser: false,
                errorCreateStorageTariffUser: 'Ошибка получения списка',
            })
        }
    },

    removeStorageTariffUser: async (params) => {
        try {
            set({
                isLoadingRemoveStorageTariffUser: true,
                errorRemoveStorageTariffUser: undefined,
            })

           await removeStorageTariffUser(params)


            set({
                isLoadingRemoveStorageTariffUser: false,
            })
        } catch (error) {
            set({
                isLoadingRemoveStorageTariffUser: false,
                errorRemoveStorageTariffUser: 'Ошибка',
            })
        }
    },

    createRecurringPayment: async (payload) => {
        try {
            const response = await createRecurringPayment(payload)

            window.open(
                response.action.payment_link_data.href,
                response.action.payment_link_data.target,
            )
        } catch (error) {}
    },
    changeRecurringPayment: async (payload) => {
        try {
            const response = await changeRecurringPayment(payload)
            set({
                recurringPayment: response,
            })
        } catch (error) {}
    },
    reset: () => {
        set(initialState)
    },

    resetErrorApplyPromocode: () => {
        set({ errorApplyPromocode: undefined })
    },
}))
